/* ──────────────────────────────────────────────────────────────
   shell/topbar.tsx · app topbar
   Left:  Organization + Location switchers
   Right: premium profile avatar (hover → name tooltip)
   ────────────────────────────────────────────────────────────── */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useTheme } from '../context/themeContext';
import { useApp } from '../context/appContext';
import { useSession } from '../context/sessionContext';
import {
  AppDropdown,
  Icon,
} from '../components/ui';
import ProfileMenu from './profileMenu';

interface TopbarProps {
  /** Show the hamburger that opens the mobile drawer. */
  showMenu?: boolean;
  onMenu?: () => void;
  onSignOut?: () => void;
}

export default function Topbar({ showMenu = false, onMenu, onSignOut }: TopbarProps) {
  const { theme, isDark } = useTheme();
  const { colors } = theme;
  const { tenant, setTenant } = useApp();
  const { org, user, fullName } = useSession();

  // Real org (single tenant for now). The switcher shows the business name.
  const tenantItems = org?.id ? [{ id: org.id, name: org.name || 'My Org' }] : [tenant];

  // Desktop: one row — switchers left, spacer, avatar right. Mobile: two rows —
  // hamburger + avatar on top, both switchers full-width below.
  const topbarStyle: ViewStyle = {
    flexShrink: 0,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
    backgroundColor: colors.card,
    // Sit above the content area so the avatar's hover tooltip isn't painted
    // over (or clipped) by the screen below it.
    ...(Platform.OS === 'web' ? ({ position: 'relative', zIndex: 100, overflow: 'visible' } as any) : {}),
    ...(showMenu
      ? { flexDirection: 'column', gap: 10, paddingHorizontal: 14, paddingVertical: 10 }
      : { flexDirection: 'row', alignItems: 'center', height: 76, gap: 14, paddingHorizontal: 28, paddingVertical: 12 }),
  };

  // On mobile each switcher flexes to share the full width of its own row.
  const switcherStyle: ViewStyle = showMenu ? { flex: 1, minWidth: 0 } : { width: 184 };

  const orgDropdown = (
    <AppDropdown
      compact
      isDark={isDark}
      fieldStyle={switcherStyle}
      textColor={colors.ink}
      placeholder={org?.name || 'Organization'}
      value={org?.id ?? ''}
      items={tenantItems.map((t) => ({ label: t.name, value: t.id }))}
      onSelect={(id) => {
        const t = tenantItems.find((x) => x.id === id);
        if (t) setTenant(t);
      }}
    />
  );

  // ── Mobile: hamburger + avatar row, then switcher row ─────────────
  if (showMenu) {
    return (
      <View style={topbarStyle}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <TouchableOpacity
            onPress={onMenu}
            accessibilityLabel="Open menu"
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={{
              width: 38,
              height: 38,
              borderRadius: 2,
              alignItems: 'center',
              justifyContent: 'center',
              borderWidth: 1,
              borderColor: colors.line,
              backgroundColor: colors.paper2,
              flexShrink: 0,
              ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
            }}
          >
            <Icon name="menu" size={18} color={colors.ink} />
          </TouchableOpacity>
          <View style={{ flex: 1 }} />
          <ProfileMenu variant="navbar" compact onLogout={onSignOut} />
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          {orgDropdown}
        </View>
      </View>
    );
  }

  // ── Desktop: org switcher left · spacer · avatar right ────────────
  return (
    <View style={topbarStyle}>
      {orgDropdown}
      <View style={{ flex: 1 }} />
      <ProfileMenu variant="navbar" onLogout={onSignOut} />
    </View>
  );
}
